import { collection, addDoc, query, where, orderBy, limit, getDocs, serverTimestamp, Timestamp, startAfter, QueryDocumentSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { ApplicationSessionManager } from '../../utils/applicationUtils';
import { logger } from '../../utils/logger';

// Standardized Action Taxonomy
export type AuditAction =
  // AUTH
  | 'AUTH_login' | 'AUTH_logout' | 'AUTH_role_change' | 'AUTH_approval' | 'AUTH_rejection'
  // CLIENT
  | 'CLIENT_create' | 'CLIENT_update' | 'CLIENT_delete' | 'CLIENT_link' | 'CLIENT_unlink'
  | 'CLIENT_status_update' | 'CLIENT_entity_update' | 'CLIENT_owner_update' | 'CLIENT_health_update' | 'CLIENT_partnership_details_update' | 'CLIENT_business_stage_update' | 'CLIENT_services_update'
  // CLIENT DELETE CASCADE (Phase 7 — opportunities data model unification, D-extra-A)
  | 'CLIENT_delete_dry_run'
  | 'CLIENT_delete_opportunities' | 'CLIENT_delete_commercials'
  | 'CLIENT_delete_subcollections' | 'CLIENT_delete_storage'
  | 'CLIENT_delete_application_record' | 'CLIENT_delete_chats'
  | 'CLIENT_delete_user_link_skipped'
  // MIGRATIONS (Phase 8 — opportunities data model unification)
  | 'MIGRATION_opportunities_backfill' | 'MIGRATION_registry_seed'
  // CLIENT NOTES
  | 'CLIENT_NOTE_add' | 'CLIENT_NOTE_update' | 'CLIENT_NOTE_archive' | 'CLIENT_NOTE_unarchive'
  // APPLICATION
  | 'APPLICATION_submit' | 'APPLICATION_approve' | 'APPLICATION_reject' | 'APPLICATION_link'
  | 'APPLICATION_archive' | 'APPLICATION_restore' | 'APPLICATION_delete'
  | 'APPLICATION_owner_update' | 'APPLICATION_review_status_update' | 'APPLICATION_sales_field_update'
  | 'APPLICATION_company_info_update' | 'APPLICATION_questionnaire_update'
  | 'APPLICATION_admin_note_add' | 'APPLICATION_admin_note_update' | 'APPLICATION_admin_note_delete' | 'APPLICATION_draft_approval_update'
  // LEADS
  | 'LEAD_create' | 'LEAD_status_update' | 'LEAD_notes_update' | 'LEAD_owner_update' | 'LEAD_details_update' | 'LEAD_delete'
  // SYNDICATES
  | 'SYNDICATE_create' | 'SYNDICATE_update' | 'SYNDICATE_delete' | 'SYNDICATE_owner_update' | 'SYNDICATE_status_update' | 'SYNDICATE_bulk_owner_update' | 'SYNDICATE_retire'
  // GROWTH_ENGINE
  | 'GROWTH_ENGINE_process_documents' | 'GROWTH_ENGINE_generate_icp'
  // CAPITAL_RAISING
  | 'CAPITAL_RAISING_start_workflow' | 'CAPITAL_RAISING_generate_mandate' | 'CAPITAL_RAISING_generate_brief' | 'CAPITAL_RAISING_export_brief' | 'CAPITAL_RAISING_reset_brief'
  // CHAT
  | 'CHAT_create_session' | 'CHAT_archive_session' | 'CHAT_impersonate'
  // ADMIN
  | 'ADMIN_user_impersonation' | 'ADMIN_bulk_actions' | 'ADMIN_data_export'
  // DOCUMENT EXPORTS - Enhanced security tracking
  | 'DOCUMENT_export_questionnaire' | 'DOCUMENT_export_profile' | 'DOCUMENT_export_growth_engine' 
  | 'DOCUMENT_export_csv' | 'DOCUMENT_export_other' | 'ADMIN_bulk_export'
  // DOCUMENT REGISTRY
  | 'DOCUMENT_upload' | 'DOCUMENT_link' | 'DOCUMENT_delete' | 'DOCUMENT_update' | 'FOLDER_create' | 'FOLDER_delete' | 'FOLDER_move'
  // SECURITY EVENTS - Rate limiting, validation, access control
  | 'SECURITY_rate_limit_exceeded' | 'SECURITY_auth_failed' | 'SECURITY_permission_denied'
  | 'SECURITY_invalid_input_rejected' | 'SECURITY_file_upload_rejected' | 'SECURITY_pin_verified'
  // SETTINGS - admin-only mutations in Settings tabs
  | 'SETTINGS_exchange_rate_update'
  | 'SETTINGS_compensation_defaults_update'
  | 'SETTINGS_notification_create' | 'SETTINGS_notification_update' | 'SETTINGS_notification_delete'
  | 'SETTINGS_profile_update'
  | 'SETTINGS_referral_code_create' | 'SETTINGS_referral_code_delete' | 'SETTINGS_referral_config_update'
  // Function-compat - Cloud Function writers use these names; include for label resolution
  | 'INVESTOR_MATCHING_generate'
  | 'CAPITAL_RAISING_generate_profile'
  // Legacy mapped
  | 'LEGACY_user_approved' | 'LEGACY_user_rejected' | 'LEGACY_application_linked' | 'LEGACY_application_unlinked' | 'LEGACY_application_preapproved' | 'LEGACY_client_profile_created' | 'LEGACY_client_profile_created_fallback' | 'LEGACY_application_rejected';

export type AuditSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface StandardizedAuditLogEntry {
  timestamp?: Timestamp | any;
  actorUserId: string;
  actorRole: string;
  targetUserId?: string;
  targetClientId?: string;
  targetApplicationId?: string;
  targetLeadId?: string;
  targetPartnerId?: string;
  action: AuditAction;
  context: Record<string, any>;
  sessionId?: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  severity: AuditSeverity;
  metadata?: Record<string, any>;
}

const ALLOWED_ACTIONS: Set<AuditAction> = new Set<AuditAction>([
  'AUTH_login','AUTH_logout','AUTH_role_change','AUTH_approval','AUTH_rejection',
  'CLIENT_create','CLIENT_update','CLIENT_delete','CLIENT_link','CLIENT_unlink',
  'CLIENT_status_update','CLIENT_entity_update','CLIENT_owner_update','CLIENT_health_update','CLIENT_partnership_details_update','CLIENT_business_stage_update','CLIENT_services_update',
  // Client delete cascade (Phase 7)
  'CLIENT_delete_dry_run',
  'CLIENT_delete_opportunities','CLIENT_delete_commercials',
  'CLIENT_delete_subcollections','CLIENT_delete_storage',
  'CLIENT_delete_application_record','CLIENT_delete_chats',
  'CLIENT_delete_user_link_skipped',
  // Migrations (Phase 8)
  'MIGRATION_opportunities_backfill','MIGRATION_registry_seed',
  'CLIENT_NOTE_add','CLIENT_NOTE_update','CLIENT_NOTE_archive','CLIENT_NOTE_unarchive',
  'APPLICATION_submit','APPLICATION_approve','APPLICATION_reject','APPLICATION_link','APPLICATION_archive','APPLICATION_restore','APPLICATION_delete',
  'APPLICATION_owner_update','APPLICATION_review_status_update','APPLICATION_sales_field_update',
  'APPLICATION_company_info_update','APPLICATION_questionnaire_update',
  'APPLICATION_admin_note_add','APPLICATION_admin_note_update','APPLICATION_admin_note_delete','APPLICATION_draft_approval_update',
  'LEAD_create','LEAD_status_update','LEAD_notes_update','LEAD_owner_update','LEAD_details_update','LEAD_delete',
  'SYNDICATE_create','SYNDICATE_update','SYNDICATE_delete','SYNDICATE_owner_update','SYNDICATE_status_update','SYNDICATE_bulk_owner_update','SYNDICATE_retire',
  'GROWTH_ENGINE_process_documents','GROWTH_ENGINE_generate_icp',
  'CAPITAL_RAISING_start_workflow','CAPITAL_RAISING_generate_mandate','CAPITAL_RAISING_generate_brief','CAPITAL_RAISING_export_brief','CAPITAL_RAISING_reset_brief',
  'CHAT_create_session','CHAT_archive_session','CHAT_impersonate',
  'ADMIN_user_impersonation','ADMIN_bulk_actions','ADMIN_data_export',
  // Document export actions
  'DOCUMENT_export_questionnaire','DOCUMENT_export_profile','DOCUMENT_export_growth_engine',
  'DOCUMENT_export_csv','DOCUMENT_export_other','ADMIN_bulk_export',
  'DOCUMENT_upload','DOCUMENT_link','DOCUMENT_delete','DOCUMENT_update','FOLDER_create','FOLDER_delete','FOLDER_move',
  // Security events
  'SECURITY_rate_limit_exceeded','SECURITY_auth_failed','SECURITY_permission_denied',
  'SECURITY_invalid_input_rejected','SECURITY_file_upload_rejected','SECURITY_pin_verified',
  // Settings
  'SETTINGS_exchange_rate_update',
  'SETTINGS_compensation_defaults_update',
  'SETTINGS_notification_create','SETTINGS_notification_update','SETTINGS_notification_delete',
  'SETTINGS_profile_update',
  'SETTINGS_referral_code_create','SETTINGS_referral_code_delete','SETTINGS_referral_config_update',
  // Function-compat
  'INVESTOR_MATCHING_generate','CAPITAL_RAISING_generate_profile',
  // Legacy mapped
  'LEGACY_user_approved','LEGACY_user_rejected','LEGACY_application_linked','LEGACY_application_unlinked','LEGACY_application_preapproved','LEGACY_client_profile_created','LEGACY_client_profile_created_fallback','LEGACY_application_rejected'
]);

function getBestEffortSessionId(): string | undefined {
  try {
    const sess = ApplicationSessionManager.getSession();
    if (sess?.sessionId) return sess.sessionId;
  } catch {}
  try {
    const legacy = localStorage.getItem('voluntas_session_id') || undefined;
    if (legacy) return legacy;
  } catch {}
  return undefined;
}

function getUserAgent(): string | null {
  try { return navigator.userAgent || null; } catch { return null; }
}

function sanitizeAuditValue(value: any): any {
  if (value === undefined) return undefined;
  if (value === null) return null;

  if (Array.isArray(value)) {
    const sanitizedArray = value
      .map((entry) => sanitizeAuditValue(entry))
      .filter((entry) => entry !== undefined);
    return sanitizedArray;
  }

  if (typeof value === 'object') {
    const sanitizedObject: Record<string, any> = {};
    Object.entries(value).forEach(([key, nestedValue]) => {
      const sanitizedNestedValue = sanitizeAuditValue(nestedValue);
      if (sanitizedNestedValue !== undefined) {
        sanitizedObject[key] = sanitizedNestedValue;
      }
    });
    return sanitizedObject;
  }

  return value;
}

// Deduplication cache to prevent duplicate logs within 5 seconds
const recentLogsCache = new Map<string, number>();
const DEDUP_WINDOW = 5000; // 5 seconds

function getDedupKey(entry: any): string {
  const currentUid = entry.actorUserId || auth.currentUser?.uid || 'unknown';
  const sessionId = entry.sessionId ?? getBestEffortSessionId() ?? 'no-session';
  const target = entry.targetClientId || entry.targetApplicationId || entry.targetLeadId || entry.targetPartnerId || entry.targetUserId || 'none';
  const contextField = typeof entry?.context?.field === 'string' ? entry.context.field : 'no-field';
  return `${currentUid}:${entry.action}:${target}:${sessionId}:${contextField}`;
}

function isDuplicate(entry: any): boolean {
  const key = getDedupKey(entry);
  const lastLogTime = recentLogsCache.get(key);
  const now = Date.now();
  
  if (lastLogTime && (now - lastLogTime) < DEDUP_WINDOW) {
    logger.debug('[Audit] Duplicate log prevented:', key);
    return true;
  }
  
  recentLogsCache.set(key, now);
  
  // Clean old entries (older than 10 seconds)
  const keysToDelete: string[] = [];
  recentLogsCache.forEach((timestamp, cacheKey) => {
    if (now - timestamp > 10000) {
      keysToDelete.push(cacheKey);
    }
  });
  keysToDelete.forEach(k => recentLogsCache.delete(k));
  
  return false;
}

export async function createAuditLog(entry: Omit<StandardizedAuditLogEntry, 'timestamp' | 'actorUserId' | 'userAgent' | 'sessionId'> & { actorUserId?: string; sessionId?: string; userAgent?: string | null; }) {
  try {
    const currentUid = entry.actorUserId || auth.currentUser?.uid || 'unknown';
    if (!ALLOWED_ACTIONS.has(entry.action)) {
      logger.warn('[Audit] Invalid action provided:', entry.action);
      return;
    }

    // Check for duplicates
    if (isDuplicate(entry)) {
      return;
    }

    // Build payload without the Firestore sentinel so sanitizer cannot corrupt it
    const basePayload: any = {
      ...entry,
      actorUserId: currentUid,
    };

    // Add optional fields only if they have valid values
    const userAgent = entry.userAgent ?? getUserAgent();
    if (userAgent) {
      basePayload.userAgent = userAgent;
    }

    const sessionId = entry.sessionId ?? getBestEffortSessionId();
    if (sessionId) {
      basePayload.sessionId = sessionId;
    }

    // Sanitize user-provided data first, then re-attach the sentinel.
    // serverTimestamp() returns a FieldValue opaque object - passing it through
    // sanitizeAuditValue would reduce it to {} and break timestamp ordering.
    const cleanedPayload = {
      ...sanitizeAuditValue(basePayload),
      timestamp: serverTimestamp(),
    };

    const auditRef = collection(db, 'audit_logs');
    await addDoc(auditRef, cleanedPayload);
  } catch (error) {
    logger.error('Failed to create audit log:', error);
  }
}

export interface AuditLogFilter {
  actorUserId?: string;
  targetUserId?: string;
  targetClientId?: string;
  targetApplicationId?: string;
  action?: AuditAction;
  severity?: AuditSeverity;
  startAt?: Date;
  endAt?: Date;
  limitCount?: number;
  /** Cursor for pagination - pass the last QueryDocumentSnapshot from a previous page */
  startAfterDoc?: QueryDocumentSnapshot;
}

/** Broken April 2026 rows stored a map instead of a Timestamp and sort first on desc. */
const REAL_AUDIT_TIMESTAMP_FLOOR = Timestamp.fromMillis(1);

function auditTimestampLowerBound(startAt?: Date): Timestamp {
  if (startAt && startAt.getTime() > REAL_AUDIT_TIMESTAMP_FLOOR.toMillis()) {
    return Timestamp.fromDate(startAt);
  }
  return REAL_AUDIT_TIMESTAMP_FLOOR;
}

export const getAuditLogs = async (filter: AuditLogFilter = {}) => {
  const auditRef = collection(db, 'audit_logs');
  const constraints: any[] = [
    where('timestamp', '>=', auditTimestampLowerBound(filter.startAt)),
    orderBy('timestamp', 'desc'),
  ];
  if (filter.actorUserId) constraints.push(where('actorUserId', '==', filter.actorUserId));
  if (filter.targetUserId) constraints.push(where('targetUserId', '==', filter.targetUserId));
  if (filter.targetClientId) constraints.push(where('targetClientId', '==', filter.targetClientId));
  if (filter.targetApplicationId) constraints.push(where('targetApplicationId', '==', filter.targetApplicationId));
  if (filter.action) constraints.push(where('action', '==', filter.action));
  if (filter.severity) constraints.push(where('severity', '==', filter.severity));
  if (filter.endAt) constraints.push(where('timestamp', '<=', Timestamp.fromDate(filter.endAt)));
  if (filter.limitCount) constraints.push(limit(filter.limitCount)); else constraints.push(limit(100));
  const q = query(auditRef, ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

/**
 * Cursor-based paged version of getAuditLogs.
 * Returns logs, the last document snapshot for use as the next page cursor, and a hasMore flag.
 */
export const getAuditLogsPaged = async (filter: AuditLogFilter = {}): Promise<{
  logs: Array<{ id: string } & Record<string, any>>;
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}> => {
  const auditRef = collection(db, 'audit_logs');
  const pageSize = filter.limitCount || 50;
  const constraints: any[] = [
    where('timestamp', '>=', auditTimestampLowerBound(filter.startAt)),
    orderBy('timestamp', 'desc'),
  ];
  if (filter.actorUserId) constraints.push(where('actorUserId', '==', filter.actorUserId));
  if (filter.targetUserId) constraints.push(where('targetUserId', '==', filter.targetUserId));
  if (filter.targetClientId) constraints.push(where('targetClientId', '==', filter.targetClientId));
  if (filter.targetApplicationId) constraints.push(where('targetApplicationId', '==', filter.targetApplicationId));
  if (filter.action) constraints.push(where('action', '==', filter.action));
  if (filter.severity) constraints.push(where('severity', '==', filter.severity));
  if (filter.endAt) constraints.push(where('timestamp', '<=', Timestamp.fromDate(filter.endAt)));
  if (filter.startAfterDoc) constraints.push(startAfter(filter.startAfterDoc));
  constraints.push(limit(pageSize));
  const q = query(auditRef, ...constraints);
  const snapshot = await getDocs(q);
  return {
    logs: snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
    lastDoc: snapshot.docs[snapshot.docs.length - 1] ?? null,
    hasMore: snapshot.docs.length === pageSize,
  };
};

/**
 * Get security-specific audit logs
 * Filters for security event actions and returns with full details
 */
export const getSecurityEventLogs = async (filter: Omit<AuditLogFilter, 'action'> & { 
  includeAllSecurityEvents?: boolean;
  securityAction?: 'SECURITY_rate_limit_exceeded' | 'SECURITY_auth_failed' | 'SECURITY_permission_denied' | 'SECURITY_invalid_input_rejected' | 'SECURITY_file_upload_rejected' | 'SECURITY_pin_verified';
} = {}) => {
  const auditRef = collection(db, 'audit_logs');
  const constraints: any[] = [
    where('timestamp', '>=', auditTimestampLowerBound(filter.startAt)),
    orderBy('timestamp', 'desc'),
  ];
  
  // Filter by specific security action or all security events
  if (filter.securityAction) {
    constraints.push(where('action', '==', filter.securityAction));
  } else if (filter.includeAllSecurityEvents !== false) {
    // Note: Firestore doesn't support 'in' with more than 10 values
    // For comprehensive querying, may need multiple queries or use severity filter
    constraints.push(where('severity', 'in', ['warning', 'error', 'critical']));
  }
  
  // Add other filters
  if (filter.actorUserId) constraints.push(where('actorUserId', '==', filter.actorUserId));
  if (filter.targetUserId) constraints.push(where('targetUserId', '==', filter.targetUserId));
  if (filter.targetClientId) constraints.push(where('targetClientId', '==', filter.targetClientId));
  if (filter.targetApplicationId) constraints.push(where('targetApplicationId', '==', filter.targetApplicationId));
  if (filter.severity) constraints.push(where('severity', '==', filter.severity));
  if (filter.endAt) constraints.push(where('timestamp', '<=', Timestamp.fromDate(filter.endAt)));
  if (filter.limitCount) constraints.push(limit(filter.limitCount)); else constraints.push(limit(100));
  
  const q = query(auditRef, ...constraints);
  const snapshot = await getDocs(q);
  const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as StandardizedAuditLogEntry }));
  
  // If not filtering by specific action, filter for security events in post-processing
  if (!filter.securityAction && filter.includeAllSecurityEvents !== false) {
    return logs.filter(log => 
      (log.action as string).startsWith('SECURITY_')
    );
  }
  
  return logs;
};

/**
 * Get rate limit violation statistics
 * Returns count and details of rate limit events
 */
export const getRateLimitViolationStats = async (startDate?: Date, endDate?: Date) => {
  const logs = await getSecurityEventLogs({
    securityAction: 'SECURITY_rate_limit_exceeded',
    startAt: startDate,
    endAt: endDate,
    limitCount: 1000
  });
  
  const stats = {
    totalViolations: logs.length,
    byOperation: {} as Record<string, number>,
    byUser: {} as Record<string, number>,
    topOffenders: [] as Array<{ userId: string; count: number }>
  };
  
  logs.forEach(log => {
    const operation = log.context?.operation as string;
    const userId = log.actorUserId;
    
    if (operation) {
      stats.byOperation[operation] = (stats.byOperation[operation] || 0) + 1;
    }
    
    if (userId) {
      stats.byUser[userId] = (stats.byUser[userId] || 0) + 1;
    }
  });
  
  // Calculate top offenders
  stats.topOffenders = Object.entries(stats.byUser)
    .map(([userId, count]) => ({ userId, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  
  return stats;
};


